import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";

import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { DEFAULT_LANDING_VIEW, landingView } from "../lib/views";

/**
 * The Helpdesk's front door: wherever this agent chose to land.
 *
 * A redirect rather than a view of its own, so the address bar ends up on the
 * real `/v/:key` and a reload, a bookmark or the back button behave exactly as
 * they would had the agent clicked that view in the sidebar. The rail's
 * Helpdesk tile points here for the same reason -- one place decides where the
 * Helpdesk opens, rather than the index route and the rail each holding a copy
 * that the next person to change one would not know to change in the other.
 *
 * Waits for the agent's settings rather than redirecting to All open and then
 * again: a flash of the wrong queue, and its fetch, every time somebody opens
 * the app is the thing the setting exists to remove. A failed fetch is not a
 * reason to keep anybody out of their tickets, so it falls through to the
 * default.
 */
export function HelpdeskHome() {
  const settings = useQuery({
    queryKey: queryKeys.mySettings,
    queryFn: api.mySettings,
  });
  const saved = useQuery({
    queryKey: queryKeys.savedViews,
    queryFn: api.savedViews,
    enabled: (settings.data?.helpdeskDefaultView ?? "").startsWith("saved-"),
  });

  if (settings.isPending) return null;

  const view = settings.isError
    ? DEFAULT_LANDING_VIEW
    : landingView(
        settings.data.helpdeskDefaultView,
        saved.data?.map((entry) => entry.id),
      );

  return <Navigate to={`/v/${view}`} replace />;
}
